import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseSummaryParams, resolvePresetRange, isValidRange } from './summaryPeriods';
import { useExecutiveSummary } from './useExecutiveSummary';
import { getBusinessSummary } from '../../services/businessAnalytics';
import { describeComparisonPeriods } from './summaryFormatters';
import { applyScopeParams, createEmptyScope, describeScopeSelection, isEmptyScope } from '../../utils/scopeSelection';
import { countRu, CAMERA_FORMS, OBJECT_FORMS, RULE_FORMS } from '../../utils/pluralRu';
import {
  COMPARISON_TABLE,
  SHIFT_TABLE,
  applyTableParams,
  createTableState,
  parseTableParams,
  setTableOffset,
  toTableQuery,
} from '../../utils/analyticsTableParams';
import { Funnel, FunnelXmark } from '@gravity-ui/icons';
import ScopeFilter from '../../components/ScopeFilter/ScopeFilter';
import { useNavigationEntitlements } from '../../hooks/useNavigationEntitlements';
import { isNavigationKeyAllowed, NAV_KEYS } from '../../utils/navigationEntitlements';
import { useDraftScope } from '../../hooks/useDraftScope';
import { useScopeRestore } from '../../hooks/useScopeRestore';
import SummaryFilters from './components/SummaryFilters';
import TimezoneNotice from './components/TimezoneNotice';
import SummaryStateBlock from './components/SummaryStateBlock';
import OverviewBlocks from './components/OverviewBlocks';
import OperationalIssuesTab from './issues/OperationalIssuesTab';
import { TAB_KIND, buildTabParams, hasOwnParams, loadTabState, saveTabState } from './summaryTabState';
import { useStaleRuleIds } from './useStaleRuleIds';
import { useAutoDismiss } from '../../hooks/useAutoDismiss';
import { APP_EVENTS, useAppEvent } from '../../utils/appEvents';
import './SummaryPage.css';

/*
 * «Сводка» / Executive Summary (ТЗ №1 + дополнение по timezone).
 * Спека: docs/superpowers/specs/2026-09-21-summary-ops-issues-timezone-design.md §2
 *
 * URL — единственный источник фильтров: scope-массивы (factory_ids,
 * workshop_ids, section_ids, room_ids, camera_ids, rule_ids), preset, from, to.
 * from/to хранятся как timezone-aware ISO (ровно то, что уходит в запрос),
 * поэтому Back/Forward воспроизводят тот же абсолютный интервал и тот же scope.
 *
 * SCOPE-MULTI01 (ТЗ v4 §21, FE-FIX18): scope «Сводки» живёт только в этом URL.
 * «Контроль» в ЛК использует тот же компонент ScopeFilter, но свой адрес и свой
 * кэш — смена scope здесь не меняет scope там.
 *
 * ANALYTICS-TABLES01 (ТЗ v4 §25–26, FE-FIX22/23): у таблиц рейтингов свои
 * параметры (comparison_*, shift_*) в том же URL — фильтр, сортировка и
 * страница восстанавливаются по ссылке, а считает их backend одним запросом
 * Executive Summary. Смена глобального scope сбрасывает локальные фильтры и
 * страницу: иначе таблица осталась бы с фильтром по объекту, которого в новой
 * области данных нет.
 *
 * ?tab=issues — «Системные проблемы» (ТЗ №2): свой глобальный период анализа,
 * не связанный с preset Executive Summary; без tab поведение не меняется.
 *
 * ?tab=business-metrics — «Бизнес-метрики» (BM-FE-TZ-002B/002C): зеркало
 * вкладки «Нарушения» — тот же компонент блоков (kind='business'), те же
 * фильтры, период и параметры таблиц, но свой запрос
 * (GET /analytics/business-summary), свой список правил и своё сохранённое
 * состояние (summaryTabState). Без блоков внимания и позитивных сигналов и
 * без оценки роста цветом.
 */

// navKey — ключ «Видимых вкладок» (Роли): снятая там вкладка здесь скрыта.
// ТЗ V5 №7: у «Бизнес-метрик» свой ключ summary.business_metrics.
const TABS = [
  { id: 'overview', label: 'Нарушения', navKey: NAV_KEYS.SUMMARY_OVERVIEW },
  { id: 'business-metrics', label: 'Бизнес-метрики', navKey: NAV_KEYS.SUMMARY_BUSINESS_METRICS },
  { id: 'issues', label: 'Системные проблемы', navKey: NAV_KEYS.ANALYTICS_OPERATIONAL_ISSUES },
];

const TAB_IDS = TABS.map((tab) => tab.id);

const SCOPE_CACHE_KEY = 'summary:overview';
const BUSINESS_SCOPE_CACHE_KEY = 'summary:business';

const VIOLATION_TABLES = { comparison: COMPARISON_TABLE, shift: SHIFT_TABLE };

const RANGE_ERROR = 'Конец периода должен быть позже начала';

const describeRemovedRules = (count) => (count === 1
  ? 'Правило перешло в другую вкладку и убрано из фильтра'
  : `${countRu(count, RULE_FORMS)} перешли в другую вкладку и убраны из фильтра`);

const SummaryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { keys: navigationKeys } = useNavigationEntitlements();
  const visibleTabs = TABS.filter((tab) => !tab.navKey || isNavigationKeyAllowed(navigationKeys, tab.navKey));
  const shownTabs = visibleTabs.length > 0 ? visibleTabs : TABS;
  const tabParam = searchParams.get('tab');
  const requestedTab = TAB_IDS.includes(tabParam) ? tabParam : 'overview';
  const activeTab = shownTabs.some((tab) => tab.id === requestedTab) ? requestedTab : shownTabs[0].id;
  const isViolations = activeTab === 'overview';
  const isBusiness = activeTab === 'business-metrics';
  // Обе аналитические вкладки устроены одинаково: scope, период, таблицы в URL.
  const isOverview = isViolations || isBusiness;
  const kind = TAB_KIND[activeTab];
  const tableDefs = VIOLATION_TABLES;
  const params = useMemo(() => parseSummaryParams(searchParams), [searchParams]);
  const comparisonTable = useMemo(
    () => parseTableParams(searchParams, tableDefs.comparison),
    [searchParams, tableDefs]
  );
  const shiftTable = useMemo(() => parseTableParams(searchParams, tableDefs.shift), [searchParams, tableDefs]);
  const [scopeOpen, setScopeOpen] = useState(false);

  const [notice, setNotice] = useState('');
  useAutoDismiss(notice, setNotice);

  // Параметры другой вкладки в URL не оставляем, чтобы URL не врал: уходящая
  // вкладка запоминает своё состояние, новая берёт только своё.
  const openTab = (tabId) => {
    if (tabId === activeTab) return;
    saveTabState(kind, searchParams);
    setSearchParams(buildTabParams(tabId, loadTabState(TAB_KIND[tabId])));
    setNotice('');
  };

  // Вход без своих параметров в URL (меню, ссылка на вкладку) — восстановить
  // сохранённое состояние вкладки целиком, с точными датами. Явные фильтры в
  // URL главнее. Пока восстановленный URL не применился, ни сохранять, ни
  // подставлять preset по умолчанию нельзя: они затёрли бы восстановление.
  const restoreCheckedRef = useRef(null);
  const restorePendingRef = useRef(false);
  useEffect(() => {
    if (restoreCheckedRef.current === activeTab) return;
    restoreCheckedRef.current = activeTab;
    if (hasOwnParams(searchParams)) return;
    const saved = loadTabState(kind);
    if (!saved || saved.toString() === '') return;
    restorePendingRef.current = true;
    setSearchParams(buildTabParams(activeTab, saved), { replace: true });
  }, [activeTab, kind, searchParams, setSearchParams]);

  useEffect(() => {
    if (restoreCheckedRef.current !== activeTab) return;
    if (restorePendingRef.current) {
      if (!hasOwnParams(searchParams)) return;
      restorePendingRef.current = false;
    }
    saveTabState(kind, searchParams);
  }, [activeTab, kind, searchParams]);

  // ТЗ №05: вход без scope в URL (меню, возврат с «Системных проблем») берёт
  // последний выбранный scope Обзора; сам scope «Контроля» сюда не попадает.
  // У «Бизнес-метрик» свой последний scope: выбор на одной вкладке не меняет другую.
  const violationScopeRestore = useScopeRestore(SCOPE_CACHE_KEY, params.scope, { enabled: isViolations });
  const businessScopeRestore = useScopeRestore(BUSINESS_SCOPE_CACHE_KEY, params.scope, { enabled: isBusiness });
  const { restorableScope, remember: rememberScope } = isBusiness ? businessScopeRestore : violationScopeRestore;

  // Пустой URL или preset без from/to → materialize preset в URL (replace),
  // чтобы история хранила абсолютный интервал, а не «7 дней от неизвестно когда».
  // Восстановление scope делается тем же вызовом: два раздельных replace по
  // одному searchParams затирали бы друг друга.
  useEffect(() => {
    if (!isOverview || restorePendingRef.current) return;
    const needsRange = params.preset !== 'custom' && (!params.from || !params.to);
    if (!needsRange && !restorableScope) return;
    let next = new URLSearchParams(searchParams);
    if (needsRange) {
      const range = resolvePresetRange(params.preset);
      next.set('preset', params.preset);
      next.set('from', range.from);
      next.set('to', range.to);
    }
    if (restorableScope) next = applyScopeParams(next, restorableScope);
    setSearchParams(next, { replace: true });
  }, [isOverview, params, restorableScope, searchParams, setSearchParams]);

  const rangeValid = isValidRange(params.from, params.to);
  const tableParams = useMemo(() => ({
    ...toTableQuery(tableDefs.comparison, comparisonTable),
    ...toTableQuery(tableDefs.shift, shiftTable),
  }), [comparisonTable, shiftTable, tableDefs]);

  const violationSummary = useExecutiveSummary({
    from: params.from,
    to: params.to,
    scope: params.scope,
    tableParams,
    enabled: isViolations && rangeValid,
  });
  const businessSummary = useExecutiveSummary({
    from: params.from,
    to: params.to,
    scope: params.scope,
    tableParams,
    enabled: isBusiness && rangeValid,
    fetcher: getBusinessSummary,
  });
  const { data, status, error, reload } = isBusiness ? businessSummary : violationSummary;
  // Правило сменило категорию в другой части приложения — цифры вкладки
  // устарели.
  useAppEvent(APP_EVENTS.RULE_CATEGORY_CHANGED, reload);

  const updateParams = useCallback((partial) => {
    const next = new URLSearchParams(searchParams);
    const merged = { ...params, ...partial };
    if (partial.preset && partial.preset !== 'custom') {
      const range = resolvePresetRange(partial.preset);
      merged.from = range.from;
      merged.to = range.to;
    }
    next.set('preset', merged.preset);
    if (merged.from) next.set('from', merged.from); else next.delete('from');
    if (merged.to) next.set('to', merged.to); else next.delete('to');
    let result = applyScopeParams(next, merged.scope);
    // Другой период — другой набор строк: страницы таблиц начинаются заново.
    // «Обновить» того же периода страницу не трогает.
    const periodChanged = merged.preset !== params.preset
      || (partial.from !== undefined && partial.from !== params.from)
      || (partial.to !== undefined && partial.to !== params.to);
    if (periodChanged) {
      [tableDefs.comparison, tableDefs.shift].forEach((table) => {
        result = applyTableParams(result, table, setTableOffset(table, parseTableParams(result, table), 0));
      });
    }
    setSearchParams(result);
  }, [params, searchParams, setSearchParams, tableDefs]);

  // Смена области данных обнуляет локальные фильтры и страницы таблиц.
  const updateScope = useCallback((scope) => {
    rememberScope(scope);
    const next = new URLSearchParams(searchParams);
    next.set('preset', params.preset);
    if (params.from) next.set('from', params.from); else next.delete('from');
    if (params.to) next.set('to', params.to); else next.delete('to');
    const withScope = applyScopeParams(next, scope);
    const withComparison = applyTableParams(withScope, tableDefs.comparison, createTableState(tableDefs.comparison));
    setSearchParams(applyTableParams(withComparison, tableDefs.shift, createTableState(tableDefs.shift)));
  }, [params.from, params.preset, params.to, rememberScope, searchParams, setSearchParams, tableDefs]);

  // Галочки меняют черновик, запрос уходит по «Применить».
  const scopeDraft = useDraftScope(params.scope, updateScope);

  const updateTable = useCallback((table, nextState) => {
    setSearchParams(applyTableParams(searchParams, table, nextState));
  }, [searchParams, setSearchParams]);

  const tables = useMemo(() => ({
    comparison: {
      table: tableDefs.comparison,
      state: comparisonTable,
      onChange: (next) => updateTable(tableDefs.comparison, next),
    },
    shift: {
      table: tableDefs.shift,
      state: shiftTable,
      onChange: (next) => updateTable(tableDefs.shift, next),
    },
  }), [comparisonTable, shiftTable, tableDefs, updateTable]);

  // «Обновить»: rolling preset пересчитывается от текущего now; custom —
  // повтор ровно тех же URL-параметров (ТЗ №1 §12.1).
  // Если from/to не изменились (та же секунда), запрос всё равно повторится
  // за счёт reloadToken. Оба обновления обёрнуты в startTransition: навигация
  // react-router и так идёт в transition-lane, а setReloadToken без обёртки
  // попадает в sync-lane — React рендерит их раздельно, effect срабатывает
  // дважды и первый запрос (с прежним диапазоном) тут же отменяется вторым.
  const refresh = useCallback(() => {
    startTransition(() => {
      if (params.preset !== 'custom') updateParams({ preset: params.preset });
      reload();
    });
  }, [params.preset, reload, updateParams]);

  const resetScope = useCallback(() => updateScope(createEmptyScope()), [updateScope]);

  // Правило перевели в другую категорию — убираем его из rule_ids этой
  // вкладки (replace: это не шаг пользователя) и коротко говорим об этом.
  const staleRuleIds = useStaleRuleIds(kind, params.scope.ruleIds, isOverview);
  useEffect(() => {
    // Сначала период и восстановление должны лечь в URL, иначе их replace
    // перетрёт этот.
    if (staleRuleIds.length === 0 || restorePendingRef.current || !params.from || !params.to) return;
    const stale = new Set(staleRuleIds);
    const remaining = params.scope.ruleIds.filter((id) => !stale.has(id));
    if (remaining.length === params.scope.ruleIds.length) return;
    const nextScope = { ...params.scope, ruleIds: remaining };
    rememberScope(nextScope);
    setSearchParams(applyScopeParams(searchParams, nextScope), { replace: true });
    setNotice(describeRemovedRules(params.scope.ruleIds.length - remaining.length));
  }, [staleRuleIds, params.from, params.to, params.scope, rememberScope, searchParams, setSearchParams]);

  const renderError = () => (
    <SummaryStateBlock
      state="error"
      message={error.message}
      onRetry={error.kind === 'not_found' ? undefined : reload}
      onReset={error.kind === 'not_found' ? resetScope : undefined}
      resetLabel="Все объекты"
    />
  );

  const renderBody = () => {
    if (params.preset === 'custom' && !rangeValid) {
      if (!params.from || !params.to) {
        return <SummaryStateBlock state="empty" message="Укажите начало и конец периода и нажмите «Применить»" />;
      }
      return <SummaryStateBlock state="error" message={RANGE_ERROR} />;
    }
    if (!data) {
      if (status === 'error') return renderError();
      return <SummaryStateBlock state="loading" />;
    }
    return (
      <>
        {status === 'error' && renderError()}
        <div className={`sum-body${status === 'loading' ? ' sum-body--stale' : ''}`} aria-busy={status === 'loading'}>
          {data.scope?.camera_count === 0
            ? <SummaryStateBlock state="empty" message="Нет доступных камер в выбранном scope" />
            : (
              <OverviewBlocks
                kind={kind}
                data={data}
                scope={params.scope}
                period={{ preset: params.preset, from: params.from, to: params.to }}
                tables={tables}
                tablesDisabled={status === 'loading'}
              />
            )}
        </div>
      </>
    );
  };

  // FE-FIX13 + FE-FIX20: какой именно период сравнивается — из scope, а не из
  // локального расчёта по выбранным датам.
  const periods = describeComparisonPeriods(data?.scope);

  // mode приходит из backend (all_objects | object | multi_scope) — подпись
  // не выводится из локального выбора, чтобы не расходиться с данными.
  const describeSubtitle = (scope) => {
    const what = isBusiness ? 'Бизнес-события и метрики' : 'Ключевые показатели и аналитика';
    if (scope?.mode === 'object' && scope.factory_name) {
      return `${what} по объекту «${scope.factory_name}»`;
    }
    if (scope?.mode === 'multi_scope') {
      return `${what} по выбранной области данных`;
    }
    return `${what} по всем объектам в одном месте`;
  };
  const subtitle = describeSubtitle(data?.scope);

  return (
    <div className="sum-container">
      <h1 className="sum-title">Сводка</h1>
      <p className="sum-subtitle">{isOverview ? subtitle : 'Устойчивые повторяющиеся нарушения'}</p>

      <div className="sum-tabs">
        {shownTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`sum-tab-btn${activeTab === tab.id ? ' sum-tab-btn--active' : ''}`}
            onClick={() => openTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {!isOverview && <OperationalIssuesTab />}

      {isOverview && (
        <>
          {/* Свёрнуто — одна кнопка «Фильтр» (со сводкой, если что-то выбрано);
              раскрыли — кнопка исчезает, свернуть можно иконкой в шапке фильтра. */}
          {!scopeOpen && (
            <div className="sum-scope-toggle">
              <button
                type="button"
                className="sum-btn sum-btn--icon"
                aria-expanded={false}
                onClick={() => setScopeOpen(true)}
              >
                <Funnel width={16} height={16} aria-hidden />
                Фильтр
                {!isEmptyScope(params.scope) && (
                  <span className="sum-scope-chip">{describeScopeSelection(params.scope)}</span>
                )}
              </button>
              {!isEmptyScope(params.scope) && (
                <button
                  type="button"
                  className="sum-btn sum-btn--icon"
                  onClick={resetScope}
                  aria-label="Сбросить фильтр"
                  title="Сбросить фильтр"
                >
                  <FunnelXmark width={16} height={16} aria-hidden />
                </button>
              )}
            </div>
          )}

          {scopeOpen && (
            <ScopeFilter
              value={scopeDraft.draft}
              onChange={scopeDraft.setDraft}
              onApply={scopeDraft.apply}
              applyDisabled={!scopeDraft.dirty}
              includeRules
              rulesKind={kind}
              idPrefix="sum-scope"
              onCollapse={() => setScopeOpen(false)}
            />
          )}

          <SummaryFilters
            preset={params.preset}
            from={params.from}
            to={params.to}
            onChange={updateParams}
            onRefresh={refresh}
            validationError={params.preset === 'custom' && params.from && params.to && !rangeValid ? RANGE_ERROR : ''}
          />

          {data?.scope && (
            <div className="sum-scope-line">
              <span>
                {countRu(data.scope.object_count, OBJECT_FORMS)} · {countRu(data.scope.camera_count, CAMERA_FORMS)}
              </span>
              {periods.current && <span>{periods.current}</span>}
              {periods.previous && <span>Сравнение с {periods.previous}</span>}
              {periods.previousNote && <span>{periods.previousNote}</span>}
              <TimezoneNotice scope={data.scope} />
            </div>
          )}

          {notice && <p className="sum-notice" role="status">{notice}</p>}

          {renderBody()}
        </>
      )}
    </div>
  );
};

export default SummaryPage;
